'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useArtifactOpen } from '#pplx/frontend/apps/ask/asset-utils/useArtifactOpen.js';
import { useMediaLightbox } from '#pplx/frontend/apps/ask/asset-utils/useMediaLightbox.js';
import { useUnpinFile } from '#pplx/frontend/apps/ask/hooks/assets-hooks/useUnpinFile.js';
import { useGetRecentItemsWithActiveStreams } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/useGetRecentItemsWithActiveStreams.js';
import { usePinnedAutomationLatestRunLinks } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/usePinnedAutomationLatestRunLinks.js';
import { useSidebarPinnedArtifactInteractionsFactory } from '#pplx/frontend/apps/ask/hooks/sidebar-pinned-actions/useSidebarPinnedArtifactInteractions.js';
import { useSidebarThreadDeletion } from '#pplx/frontend/apps/ask/hooks/sidebar-thread-actions/useSidebarThreadDeletion.js';
import { usePinnedItemsQuery } from '#pplx/frontend/apps/ask/hooks/threads-hooks/usePinnedItemsQuery.js';
import { PinnedArtifactItem } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/ArtifactItems.js';
import { AutomationItem } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/AutomationItem.js';
import { ThreadItem } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/ThreadItem.js';
import { isComputerTaskThreadRoute } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/constants.js';
import { canonicalizeLegacyPermalinkPath } from '#pplx/frontend/apps/ask/permalink-slug/canonicalizeLegacyPermalink.js';
import { getAutomationSidebarPath } from '#pplx/frontend/apps/ask/queries/automation-queries/automationRunRoute.js';
import { isValidPinnedThreadItem } from '#pplx/frontend/apps/ask/thread-list/pinnedThreadItem.js';
import { useThreadPinActions } from '#pplx/frontend/apps/ask/thread-list/useThreadPinAction.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
export const LegacyPinnedItems = ({ isSidebarOpen, isInPopover, collapsed, renderContainer, foreground, groupedHistoryEnabled = false, }) => {
    const { $t } = useIntl();
    const { data: pinnedItems = EMPTY_ARRAY, isLoading } = usePinnedItemsQuery({
        reason: 'sidebar-pinned-items',
    });
    const { data: recentItems, isLoading: isRecentItemsLoading } = useGetRecentItemsWithActiveStreams({
        reason: 'sidebar-pinned-status',
        excludeAsi: false,
    });
    const liveStates = useMemo(() => new Map(recentItems
        .filter((item) => item.sidebarItemType !== 'automation')
        .map((thread) => [thread.context_uuid ?? thread.uuid, thread.isStreaming])), [recentItems]);
    const liveAutomations = useMemo(() => new Map(recentItems
        .filter((item) => item.sidebarItemType === 'automation')
        .map((item) => [item.automationId, item])), [recentItems]);
    const { deleted, handleDeleteThreadClick } = useSidebarThreadDeletion();
    const { unpinThreads: unpinComputerTasks } = useThreadPinActions({
        surface: 'sidebar',
        threadType: 'computer',
    });
    const { unpinThreads: unpinSearchThreads } = useThreadPinActions({
        surface: 'sidebar',
        threadType: 'search',
    });
    const { mutate: unpinFile } = useUnpinFile({
        reason: 'sidebar-pinned-artifacts',
        source: 'sidebar',
    });
    const { open, lightboxElement } = useMediaLightbox({
        reason: 'sidebar-pinned-artifacts-lightbox',
    });
    const openArtifact = useArtifactOpen({ onMediaClick: open, surface: 'sidebar' });
    const artifactInteractionsFor = useSidebarPinnedArtifactInteractionsFactory(openArtifact);
    const fallbackTitle = $t({
        defaultMessage: 'Untitled artifact',
        description: 'Fallback title for a pinned artifact in the sidebar',
        id: '50171aad03',
    });
    const unpinLabel = $t({
        defaultMessage: 'Unpin',
        description: 'Context menu label to unpin an item from the sidebar',
        id: '332fdf8b43',
    });
    const visibleItems = useMemo(() => {
        const { items, malformedThreadItemCount } = pinnedItems.reduce((result, item) => {
            if (item.kind === 'automation') {
                if (item.automation != null)
                    result.items.push(item);
                return result;
            }
            if (item.kind === 'artifact') {
                if (item.artifact != null)
                    result.items.push(item);
                return result;
            }
            if (item.kind === 'thread' && !isValidPinnedThreadItem(item)) {
                result.malformedThreadItemCount += 1;
                return result;
            }
            if (item.kind === 'thread') {
                if (!deleted.has(item.thread.uuid))
                    result.items.push(item);
                return result;
            }
            // Ignore pin kinds introduced by newer backends until this renderer supports them.
            return result;
        }, { items: [], malformedThreadItemCount: 0 });
        if (malformedThreadItemCount > 0) {
            logger.warn('[PinnedItems] Skipping malformed pinned thread items', {
                malformedThreadItemCount,
            });
        }
        return items;
    }, [deleted, pinnedItems]);
    // Only pinned automations missing from loaded Recents need the extra
    // latest-run read; while Recents is loading, coverage is unknown.
    const uncoveredPinnedAutomationIds = useMemo(() => {
        if (isRecentItemsLoading)
            return EMPTY_ARRAY;
        return visibleItems.flatMap((item) => {
            if (item.kind !== 'automation' || liveAutomations.has(item.automation.automation_id)) {
                return EMPTY_ARRAY;
            }
            return [item.automation.automation_id];
        });
    }, [isRecentItemsLoading, liveAutomations, visibleItems]);
    const latestRunLinks = usePinnedAutomationLatestRunLinks(uncoveredPinnedAutomationIds);
    const handleUnpinFile = useCallback((asset) => unpinFile(asset), [unpinFile]);
    if (isLoading || visibleItems.length === 0)
        return null;
    const content = (_jsxs(_Fragment, { children: [visibleItems.map((item, position) => {
                if (item.kind === 'automation') {
                    const liveAutomation = liveAutomations.get(item.automation.automation_id);
                    // Prefer the live Recents link, then the batched latest run. Without a
                    // latest-run thread, open the automation page.
                    const latestRunLink = liveAutomation?.link ??
                        latestRunLinks[item.automation.automation_id] ??
                        getAutomationSidebarPath(item.automation.automation_id);
                    return (_jsx(AutomationItem, { source: "pinned", automation: item.automation, href: latestRunLink, isSidebarOpen: isSidebarOpen, isInPopover: isInPopover, collapsed: collapsed, foreground: foreground, groupedHistoryEnabled: groupedHistoryEnabled, isUnread: liveAutomation?.unread ?? item.automation.unread, loading: liveAutomation?.isStreaming, showTypeIcon: foreground }, `automation-${item.automation.automation_id}`));
                }
                if (item.kind === 'artifact') {
                    return (_jsx(PinnedArtifactItem, { asset: item.artifact, position: position, isSidebarOpen: isSidebarOpen, isInPopover: isInPopover, collapsed: collapsed, foreground: foreground, onUnpin: handleUnpinFile, interactionsFor: artifactInteractionsFor, unpinLabel: unpinLabel, fallbackTitle: fallbackTitle, showTypeIcon: foreground }, `artifact-${item.artifact.asset_id}`));
                }
                if (item.kind === 'thread') {
                    const thread = {
                        ...pinnedThreadSummaryToRecentThread(item.thread),
                        isStreaming: liveStates.get(item.thread.context_uuid),
                    };
                    const additionalMenuItems = [
                        {
                            text: unpinLabel,
                            icon: $icon('pinned-off'),
                            onClick: () => {
                                const unpinThreads = isComputerTaskThreadRoute(thread.link)
                                    ? unpinComputerTasks
                                    : unpinSearchThreads;
                                unpinThreads([{ contextUUID: item.thread.context_uuid }]);
                            },
                        },
                    ];
                    return (_jsx(ThreadItem, { item: thread, onDeleteClick: handleDeleteThreadClick, sidebarOpen: isSidebarOpen, isInPopover: isInPopover, additionalMenuItems: additionalMenuItems, hoverDetailsLookupEntryUuid: item.thread.uuid, collapsed: collapsed, foreground: foreground, groupedHistoryEnabled: groupedHistoryEnabled }, `thread-${thread.uuid}`));
                }
                return null;
            }), lightboxElement] }));
    if (!renderContainer)
        return content;
    return renderContainer(content);
};
function pinnedThreadSummaryToRecentThread(thread) {
    const basePath = thread.mode === 'asi' ? '/computer/tasks' : thread.mode === 'article' ? '/page' : '/search';
    return {
        collection_uuid: thread.collection_uuid,
        uuid: thread.uuid,
        title: thread.title ?? thread.description ?? '',
        link: canonicalizeLegacyPermalinkPath(`${basePath}/${thread.slug}`),
        variant: thread.mode === 'article' ? 'article' : 'thread',
        context_uuid: thread.context_uuid,
        unread: thread.unread,
    };
}
//# sourceMappingURL=LegacyPinnedItems.js.map